import { alert } from '@/services/alert';
import { downloadAttachment } from '@/services/attachments';
import { trackDocumentDownloaded } from '@/services/analytics';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { Button, Card, ScrollArea, Stack, Text } from '@mantine/core';
import { DocumentReference, Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { usePostHog } from 'posthog-js/react';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useDocuments as usePartnerDocuments } from '@/pages/PartnersPage/PartnerTimeline/context-hooks/useDocuments';
import { TimelineSkeleton } from '@/components/TimelineSkeleton';
import { DocumentCard } from './DocumentCard';
import { SearchAndFilters } from './SearchAndFilters';
import { UploadDocumentModal } from './UploadDocumentModal';
import { isProvider } from '@bariendo/lib/profile';
import { IconPlus } from '@tabler/icons-react';

type DocumentsCardProps = {
  resource?: Patient | Organization;
};

export function DocumentsCard({ resource }: DocumentsCardProps): ReactElement {
  const medplum = useMedplum();
  const posthog = usePostHog();
  const profile = useMedplumProfile();
  const { currentUserPermissions } = useCurrentUser();
  const { documentsContext } = useTimelineContext();
  const [uploadModalOpened, setUploadModalOpened] = useState(false);

  const canUserAccessRestrictedContent = currentUserPermissions.canAccessRestrictedContent;
  const canManageRestrictedContent = currentUserPermissions.isAdmin || currentUserPermissions.hasAdminMembership;

  const isOrganization = resource?.resourceType === 'Organization';
  const partnerDocuments = usePartnerDocuments(isOrganization ? (resource as Organization | undefined) : undefined);

  // Use documentsContext from patient timeline when resource is Patient (avoids duplicate subscriptions)
  const { documents, documentsLoading, documentsError, refreshDocuments } = isOrganization
    ? partnerDocuments
    : (documentsContext ?? {
        documents: [],
        documentsLoading: true,
        documentsError: undefined,
        refreshDocuments: () => {},
      });

  const practitioner = isProvider(profile) ? (profile as Practitioner) : undefined;

  const handleDownload = async (doc: DocumentReference) => {
    const attachment = doc.content?.[0]?.attachment;
    if (!attachment) {
      alert.error('File not found.');
      return;
    }

    await downloadAttachment(medplum, attachment);

    // Track document downloaded (if we have context)
    if (resource?.id && attachment.contentType) {
      const isOrg = resource.resourceType === 'Organization';
      trackDocumentDownloaded(posthog, {
        document_type: attachment.contentType,
        source: 'documents pane',
        ...(isOrg ? { organization_id: resource.id } : { patient_id: resource.id }),
      });
    }
  };

  // Show skeleton while resource is loading or data is loading
  if (!resource || documentsLoading) {
    return <TimelineSkeleton />;
  }

  const patient = isOrganization ? undefined : (resource as Patient);
  const canUpload = patient && practitioner;

  return (
    <Card p={8}>
      {documentsError ? (
        <Text c="red" size="sm" ta="center" py="md">
          Couldn't load documents
        </Text>
      ) : documents.length === 0 ? (
        <Stack align="center" gap="md" py="md">
          <Text c="dimmed" size="sm" ta="center">
            No documents found
          </Text>
          {canUpload && (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setUploadModalOpened(true)}
              variant="light"
              c="green.9"
            >
              Add document
            </Button>
          )}
        </Stack>
      ) : (
        <DocumentList
          documents={documents}
          onDownload={handleDownload}
          resourceId={resource.id}
          resourceType={resource.resourceType}
          patient={patient}
          practitioner={practitioner}
          canUserAccessRestrictedContent={canUserAccessRestrictedContent}
          canManageRestrictedContent={canManageRestrictedContent}
          onUploadSuccess={refreshDocuments}
        />
      )}
      {canUpload && (
        <UploadDocumentModal
          opened={uploadModalOpened}
          onClose={() => setUploadModalOpened(false)}
          onUploadSuccess={() => {
            refreshDocuments();
            setUploadModalOpened(false);
          }}
          patient={patient}
          practitioner={practitioner}
        />
      )}
    </Card>
  );
}

type DocumentListProps = {
  documents: DocumentReference[];
  onDownload: (doc: DocumentReference) => void;
  resourceId?: string;
  resourceType?: 'Patient' | 'Organization';
  patient?: Patient;
  practitioner?: Practitioner;
  onUploadSuccess?: () => void;
  canUserAccessRestrictedContent: boolean;
  canManageRestrictedContent: boolean;
};

function DocumentList({
  documents,
  onDownload,
  resourceId,
  resourceType,
  patient,
  practitioner,
  onUploadSuccess,
  canUserAccessRestrictedContent,
  canManageRestrictedContent,
}: DocumentListProps): ReactElement {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  // Filter documents based on search term, selected user, selected date, and selected type
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Filter by selected user
    if (selectedUser) {
      filtered = filtered.filter((doc) => {
        const docAuthorRef = doc.author?.[0]?.reference;
        if (!docAuthorRef) return false;
        const docAuthorId = docAuthorRef.split('/')[1];
        return docAuthorId === selectedUser;
      });
    }

    // Filter by selected date
    if (selectedDate) {
      filtered = filtered.filter((doc) => {
        if (!doc.date) return false;
        const docDate = new Date(doc.date);
        return (
          docDate.getFullYear() === selectedDate.getFullYear() &&
          docDate.getMonth() === selectedDate.getMonth() &&
          docDate.getDate() === selectedDate.getDate()
        );
      });
    }

    // Filter by selected type
    if (selectedType) {
      filtered = filtered.filter((doc) => {
        const docType = doc.content?.[0]?.attachment?.contentType;
        if (!docType) return false;
        const formattedType = docType.split('/')[1]?.toUpperCase() || docType;
        return formattedType === selectedType;
      });
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((doc) => {
        const title = doc.content?.[0]?.attachment?.title?.toLowerCase() || '';
        const author = doc.author?.[0]?.display?.toLowerCase() || '';
        return title.includes(searchLower) || author.includes(searchLower);
      });
    }

    return filtered.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : undefined;
      const dateB = b.date ? new Date(b.date).getTime() : undefined;
      if (dateA === undefined) return 1;
      if (dateB === undefined) return -1;

      return dateB - dateA;
    });
  }, [documents, searchTerm, selectedUser, selectedDate, selectedType]);

  const handleSearchChange = useCallback((search: string) => {
    setSearchTerm(search);
  }, []);

  const handleUserFilterChange = useCallback((userId: string | null) => {
    setSelectedUser(userId);
  }, []);

  const handleDateFilterChange = useCallback((date: Date | null) => {
    setSelectedDate(date);
  }, []);

  const handleTypeFilterChange = useCallback((type: string | null) => {
    setSelectedType(type);
  }, []);

  return (
    <Stack gap="xs">
      <SearchAndFilters
        documents={documents}
        onSearchChange={handleSearchChange}
        onUserFilterChange={handleUserFilterChange}
        onDateFilterChange={handleDateFilterChange}
        onTypeFilterChange={handleTypeFilterChange}
        patient={patient}
        practitioner={practitioner}
        onUploadSuccess={onUploadSuccess}
      />
      {filteredDocuments.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="md">
          No documents match your search
        </Text>
      ) : (
        // 280px is the height of the header, search and filters.
        <ScrollArea type="never" h="calc(100vh - 280px)">
          <Stack gap="xs">
            {filteredDocuments.map((doc) => (
              <DocumentCard
                key={doc.id}
                doc={doc}
                onDownload={onDownload}
                patientId={resourceType === 'Patient' ? resourceId : undefined}
                organizationId={resourceType === 'Organization' ? resourceId : undefined}
                canUserAccessRestrictedContent={canUserAccessRestrictedContent}
                canManageRestrictedContent={canManageRestrictedContent}
              />
            ))}
          </Stack>
        </ScrollArea>
      )}
    </Stack>
  );
}
